import type { TodayLayoutData, TodayLayoutItem } from '@scrambl/shared'

export const HOME_DEFAULT_PREVIEW_LIMIT = 3
export const HOME_PREVIEW_LIMITS = [3, 5, 10] as const
export type HomePreviewLimit = (typeof HOME_PREVIEW_LIMITS)[number]

export function validPreviewLimit(
  value: unknown,
): HomePreviewLimit | undefined {
  return HOME_PREVIEW_LIMITS.includes(value as HomePreviewLimit)
    ? (value as HomePreviewLimit)
    : undefined
}

export function getWidgetPreviewLimit(
  layout: TodayLayoutData | null,
  item?: TodayLayoutItem,
): HomePreviewLimit {
  return (
    validPreviewLimit(item?.previewLimit) ??
    validPreviewLimit(layout?.previewLimit) ??
    HOME_DEFAULT_PREVIEW_LIMIT
  )
}

export function setHomeWidgetVisibility(
  layout: TodayLayoutData,
  item: TodayLayoutItem,
  visible: boolean,
): TodayLayoutData {
  const exists = layout.items.some(current => current.id === item.id)
  return {
    ...layout,
    items: exists
      ? layout.items.map(current =>
          current.id === item.id ? { ...current, hidden: !visible } : current,
        )
      : [...layout.items, { ...item, hidden: !visible }],
  }
}

/** Swap only the selected visible sections; gated/hidden preferences keep their position. */
export function moveHomeWidget(
  layout: TodayLayoutData,
  id: string,
  neighborId: string,
): TodayLayoutData {
  const items = [...layout.items]
  const from = items.findIndex(item => item.id === id)
  const to = items.findIndex(item => item.id === neighborId)
  if (from < 0 || to < 0) return layout
  ;[items[from], items[to]] = [items[to], items[from]]
  return { ...layout, items }
}

/** Drop `id` at `targetId`'s position (arrayMove semantics), keeping every
 *  other entry — including gated and hidden ones — in place. */
export function placeHomeWidget(
  layout: TodayLayoutData,
  id: string,
  targetId: string,
): TodayLayoutData {
  const items = [...layout.items]
  const from = items.findIndex(item => item.id === id)
  const to = items.findIndex(item => item.id === targetId)
  if (from < 0 || to < 0 || from === to) return layout
  const [moved] = items.splice(from, 1)
  items.splice(to, 0, moved)
  return { ...layout, items }
}

/**
 * Show a newly launched widget in a layout that has never seen it, right
 * after `afterId`. A layout that already lists the widget — even hidden,
 * which is how Customize records "removed" — is returned untouched, so a
 * removal always sticks. Display-only until the next save.
 */
export function promoteWidgetAfter(
  layout: TodayLayoutData,
  item: TodayLayoutItem,
  afterId: string,
): TodayLayoutData {
  if (layout.items.some(current => current.id === item.id)) return layout
  const items = [...layout.items]
  const index = items.findIndex(current => current.id === afterId)
  items.splice(index >= 0 ? index + 1 : items.length, 0, item)
  return { ...layout, items }
}

/** Undo `promoteWidgetAfter` on a layout the store has never saved it in. */
export function withoutUnsavedPromotion(
  layout: TodayLayoutData,
  stored: TodayLayoutData,
  widgetId: string,
): TodayLayoutData {
  if (stored.items.some(current => current.id === widgetId)) return layout
  return {
    ...layout,
    items: layout.items.filter(current => current.id !== widgetId),
  }
}
